/**
 * portal/CommunityPortalPage.js — the "Community" hub.
 *
 * The forum itself is a self-hosted Discourse (forums, sub-categories,
 * search, notifications, moderation, mobile apps — built for thousands of
 * active members). Forge is its sign-in provider, so members never make a
 * second account, and Forge decides which spaces they can see (groups).
 *
 * When Discourse isn't connected yet (no DISCOURSE_URL on the server),
 * this page sends members to the built-in message board instead, so the
 * Community link always leads somewhere useful.
 */
import { useState, useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import {
  HiOutlineArrowTopRightOnSquare, HiOutlineChatBubbleLeftRight, HiOutlineLockClosed, HiOutlineLockOpen,
  HiOutlineUserGroup, HiOutlineShieldCheck, HiOutlineSparkles, HiOutlineHandRaised, HiOutlineHome,
  HiOutlineDevicePhoneMobile, HiOutlineBellAlert, HiOutlineMagnifyingGlass,
} from "react-icons/hi2";
import PortalLayout from "./PortalLayout";
import { portalAPI } from "./portalApi";
import "./CommunityPortalPage.css";

const SPACES = [
  { key: "members",    Icon: HiOutlineHome,        title: "Welcome lounge",      desc: "Introductions, questions about Forge, and general conversation.", unlock: "Open to every member." },
  { key: "applicants", Icon: HiOutlineHandRaised,  title: "Applicants",          desc: "Talk with others going through application and screening.", unlock: "Opens when you submit your application." },
  { key: "cleared",    Icon: HiOutlineShieldCheck, title: "Cleared & preparing", desc: "Travel, packing and preparation with people who are cleared to attend.", unlock: "Opens when you're fully cleared." },
  { key: "attendees",  Icon: HiOutlineUserGroup,   title: "Your retreat",        desc: "Connect with the people you'll be at the retreat with.", unlock: "Opens when you book a retreat." },
  { key: "alumni",     Icon: HiOutlineSparkles,    title: "Alumni",              desc: "Integration, giving back and staying connected after your retreat.", unlock: "Opens after you attend a retreat." },
];

export default function CommunityPortalPage() {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    portalAPI.community.info()
      .then((res) => setInfo(res?.data || { enabled: false }))
      .catch((err) => setError(err.message || "Could not load the community."));
  }, []);

  // Forum not connected yet → use the built-in board
  if (info && !info.enabled) return <Navigate to="/portal/board" replace />;

  const inGroup = (key) => (info?.groups || []).some((g) => g.endsWith(`_${key}`));

  return (
    <PortalLayout eyebrow="Community" pageTitle="The Forge Community"
      quote="We rise by lifting others." quoteAuthor="Robert Ingersoll">
      {error && <div className="pc cmh__error">{error}</div>}

      {info?.enabled && (
        <>
          <section className="cmh__hero">
            <div className="cmh__hero-icon"><HiOutlineChatBubbleLeftRight /></div>
            <div className="cmh__hero-body">
              <h2 className="cmh__hero-title">Talk with people who understand</h2>
              <p className="cmh__hero-sub">
                Forums for every stage of the journey — ask questions, share what's working, and
                stay close to the people you meet. You're signed in automatically with your Forge account.
              </p>
            </div>
            <a href={info.loginUrl} className="cmh__hero-cta">
              Open the community <HiOutlineArrowTopRightOnSquare />
            </a>
          </section>

          <div className="cmh__features">
            <span><HiOutlineMagnifyingGlass /> Search every conversation</span>
            <span><HiOutlineBellAlert /> Email &amp; push notifications</span>
            <span><HiOutlineDevicePhoneMobile /> Works great on your phone</span>
          </div>

          <section className="pc cmh__spaces">
            <p className="p-section-title">Your spaces</p>
            <p className="cmh__spaces-sub">More spaces open as you move through your journey — nothing to request, it happens automatically.</p>
            <ul className="cmh__space-list">
              {SPACES.map((s) => {
                const open = inGroup(s.key);
                return (
                  <li key={s.key} className={`cmh__space ${open ? "is-open" : ""}`}>
                    <span className="cmh__space-icon"><s.Icon /></span>
                    <span className="cmh__space-text">
                      <span className="cmh__space-title">{s.title}</span>
                      <span className="cmh__space-desc">{open ? s.desc : s.unlock}</span>
                    </span>
                    <span className={`cmh__space-state ${open ? "is-open" : ""}`}>
                      {open ? <><HiOutlineLockOpen /> Open</> : <><HiOutlineLockClosed /> Locked</>}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>

          <p className="cmh__guidelines">
            Please be kind, keep what's shared in the community confidential, and never share anyone's
            personal details. See something that worries you? Flag the post or email{" "}
            <a href="mailto:support@forge.ngo">support@forge.ngo</a>. In crisis? Call or text <strong>988</strong> (Veterans press 1).
            {" "}<Link to="/portal/resources">More support resources</Link>
          </p>
        </>
      )}

      {!info && !error && <div className="cmh__loading" aria-busy="true" />}
    </PortalLayout>
  );
}
